import React, { useState, useRef, useEffect } from "react"
import styled from "styled-components"
import CaretDownRef from "@assets/icons/Caret-down.svg"
import CaretUpRef from "@assets/icons/Caret-up.svg"

export interface Option {
  value: string
  label: string
}

interface CustomSelectProps {
  options: Option[]
  value: string // Currently selected value
  onChange: (newVal: string) => void
  placeholder?: string
  disabled?: boolean
  width?: string // e.g. "220px"
}

// Styled container for the entire select
const SelectContainer = styled.div<{ disabled?: boolean; width?: string }>`
  position: relative;
  width: ${({ width }) => width || "220px"};
  margin: 4px 0;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`

// The box that shows the selected item (or placeholder)
const SelectedBox = styled.div`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

// The dropdown container
const OptionsContainer = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  width: 100%;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  z-index: 9999;
  max-height: 200px;
  overflow-y: auto;
`

// Each option row
const OptionRow = styled.div<{ selected: boolean }>`
  padding: 8px 12px;
  font-size: 14px;
  background: ${({ selected, theme }) => (selected ? theme.primary : "#fff")};
  color: ${({ selected, theme }) => (selected ? theme.white : theme.primaryText || "#000")};
  &:hover {
    background: ${({ selected, theme }) => (selected ? theme.primary : "#f2f2f2")};
  }
`

const CaretDown = styled(CaretDownRef)`
  width: 1.05rem;
  height: 1.5rem;
`

const CaretUp = styled(CaretUpRef)`
  width: 1.05rem;
  height: 1.5rem;
`

export const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select an option...",
  disabled = false,
  width,
}) => {
  const [open, setOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find((opt) => opt.value === value)

  function handleToggle() {
    if (!disabled) {
      setOpen((prev) => !prev)
    }
  }

  function handleSelect(optionValue: string) {
    onChange(optionValue)
    setOpen(false)
  }

  // Close dropdown if user clicks outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <SelectContainer ref={selectRef} disabled={disabled} width={width} onClick={handleToggle}>
      <SelectedBox>
        {selectedOption ? selectedOption.label : <span style={{ color: "#999" }}>{placeholder}</span>}
        <span style={{ display: "flex", marginLeft: "auto" }}>{open ? <CaretUp /> : <CaretDown />}</span>
      </SelectedBox>
      {open && !disabled && (
        <OptionsContainer>
          {options.map((opt) => {
            const isSelected = opt.value === value
            return (
              <OptionRow
                key={opt.value}
                selected={isSelected}
                onClick={(e) => {
                  e.stopPropagation() // so it won't re-toggle
                  handleSelect(opt.value)
                }}
              >
                {opt.label}
              </OptionRow>
            )
          })}
        </OptionsContainer>
      )}
    </SelectContainer>
  )
}
